import type { AddressFields } from "@/types/Address";
import type { YourAddressProps } from "./YourAddressProps";
import { useTranslation } from "@/hooks/useTranslation";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { FormControl } from "@/components/ui/FormControl";
import { Field } from "@/components/Field";
import * as z from "zod";

export type YourAddressAutomaticProps = {
  onSelect: (address: AddressFields) => void;
  config: YourAddressProps["config"];
};

export function YourAddressAutomatic(props: YourAddressAutomaticProps) {
  const { onSelect, config } = props;

  const t = useTranslation("your_address");

  const fields = config["your-address-fields"];

  const someAddressFieldIsRequired = [
    fields["address-line-1"]["is-required"],
    fields["address-line-2"]["is-required"],
    fields.city["is-required"],
    fields.region["is-required"],
    fields["postal-code"]["is-required"],
    fields.country["is-required"],
  ].some(Boolean);

  return (
    <Field<AddressFields>
      serverErrorPaths={[
        "your_address.postal_code",
        "your_address.city",
        "your_address.country",
        "your_address.address_line_1",
        "your_address.address_line_2",
        "your_address.region",
      ]}
      name="postalCode"
      validate={() => {
        if (someAddressFieldIsRequired) {
          return z.any().refine(() => false, {
            message: t("errors.address-autocomplete-required") as string,
          });
        }
        return null;
      }}
      render={({ errorMessage, ref }) => (
        <FormControl
          label={t(config["your-address-fields"]["postal-code"].label.i18n)}
          error={errorMessage}
        >
          <AddressAutocomplete
            ref={ref as React.RefObject<HTMLInputElement>}
            onSelect={onSelect}
          />
        </FormControl>
      )}
    />
  );
}
